import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../components/ConfirmProvider";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, Car as CarIcon, CalendarDays, Search, Plus, Send, X, Trash2 } from "lucide-react";
import html2canvas from "html2canvas";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";

import {
  DAY_NAMES, MONTH_NAMES,
  COLORS, PROJECT_INACTIVE_CATEGORIES, DISMANTLE_WINDOW_DAYS,
  toISO, mondayOf, addDays, fmtRoDay, dowFromIso, isSaturdayIso, fmtRangeLabel,
  getDayColWidthPreset,
  firmaColor as _firmaColor, regieColor as _regieColor,
  regieDefaultMinForIso,
  TOOL_SET_HEX,
} from "./planificator/utils";
import AssignDialog from "./planificator/AssignDialog";
import QuickEditPopover from "./planificator/QuickEditPopover";
import CreateDayDialog from "./planificator/CreateDayDialog";
import WeeklyMatrix from "./planificator/WeeklyMatrix";
import { RegieDetailDialog } from "./projects/ProjectSubcomponents";
import ToolHistoryDialog from "./planificator/ToolHistoryDialog";
import DismantleDialog from "./planificator/DismantleDialog";
import ShareDayDialog from "./planificator/ShareDayDialog";
import CopyDayDialog from "./planificator/CopyDayDialog";
import PersonAvailabilityMenu from "./planificator/PersonAvailabilityMenu";
import PostponeDialog from "./planificator/PostponeDialog";
import RegieFullDialog from "./planificator/RegieFullDialog";
import MagaziaCard from "./planificator/MagaziaCard";
import EditMinPeopleDialog from "./planificator/EditMinPeopleDialog";
import NeedBreakdownTooltip from "./planificator/NeedBreakdownTooltip";
import AdminMaintenanceButtons from "./planificator/AdminMaintenanceButtons";
import SidebarSummary from "./planificator/SidebarSummary";
import useWeekData from "./planificator/useWeekData";
import useResourceSplit from "./planificator/useResourceSplit";
export default function Planificator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlanificator = user?.role === "planificator";
  // Permisiune editare: admin, planificator sau orice user cu role permits MANAGEMENT
  const canEditPlanning = !!user && (user.role === "admin" || user.role === "planificator" || user.role === "lorena");
  const PLANIFICATOR_VISIBLE_CATS = ["5", "6", "6.1", "7"];
  const [anchor, setAnchor] = useState(() => {
    // Vineri (5), Sâmbătă (6), Duminică (0) → afișează deja săptămâna viitoare.
    const today = new Date();
    const dow = today.getDay();
    const baseMonday = mondayOf(today);
    if (dow === 5 || dow === 6 || dow === 0) {
      return addDays(baseMonday, 7);
    }
    return baseMonday;
  });
  const [pulseWeekLabel, setPulseWeekLabel] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("planificator_view") || "weekly"; } catch { return "weekly"; }
  });
  useEffect(() => {
    try { localStorage.setItem("planificator_view", viewMode); } catch {}
  }, [viewMode]);
  useEffect(() => {
    const t = setTimeout(() => setPulseWeekLabel(false), 2400);
    return () => clearTimeout(t);
  }, []);
  const { data, projects, regii, company, holidays, logoDataUrl, loading, reload: load } = useWeekData(toISO(anchor), toISO(addDays(anchor, 6)));
  const [editing, setEditing] = useState(null); // schedule entry
  const [creatingOn, setCreatingOn] = useState(null); // ISO day
  const [postponing, setPostponing] = useState(null); // { type, ref_id, label, currentStartDate }
  const [editingMinPeople, setEditingMinPeople] = useState(null); // { target: {kind, id, name}, isoDay, currentMin, currentMax }
  const [editingRegieId, setEditingRegieId] = useState(null); // id-ul regiei pe care o editezi full (firma, dates, default min/max, etc.)
  const [sharing, setSharing] = useState(null); // { day: ISO }
  const [historyRegie, setHistoryRegie] = useState(null); // regie object → istoric ore/persoane/mașini
  const [toolHistoryOpen, setToolHistoryOpen] = useState(false); // 🧰 istoric scule
  const [copyingFrom, setCopyingFrom] = useState(null); // ISO day source
  // Drag&drop pentru reordonare regii — în vederea zilnică, în interiorul aceleiași firme
  const [draggingRegieId, setDraggingRegieId] = useState(null);
  const [dragOverRegieId, setDragOverRegieId] = useState(null);
  const reorderRegiiInFirma = async (firmaName, sourceRid, targetRid) => {
    if (!sourceRid || !targetRid || sourceRid === targetRid) return;
    const fNorm = (firmaName || "").trim().toLowerCase();
    // Regiile firmei, în ordinea curentă (folosind compareRegii)
    const sameFirma = visibleRegii
      .filter((r) => ((r.firma || "").trim().toLowerCase() || "(fără firmă)") === (fNorm || "(fără firmă)"))
      .sort((a, b) => compareRegii(a.id, b.id, a.name, b.name));
    const srcIdx = sameFirma.findIndex((r) => r.id === sourceRid);
    const tgtIdx = sameFirma.findIndex((r) => r.id === targetRid);
    if (srcIdx === -1 || tgtIdx === -1) return; // firma diferită → ignorăm
    const reordered = [...sameFirma];
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(tgtIdx, 0, moved);
    const ids = reordered.map((r) => r.id);
    try {
      await api.post("/regii/reorder", { ordered_ids: ids });
      toast.success("📌 Ordinea regiilor salvată");
      load();
    } catch (e) {
      toast.error("Nu am putut salva ordinea: " + (e?.response?.data?.detail || e.message));
    }
  };
  const [dayColPreset] = useState(() => getDayColWidthPreset());
  const weekDays = useMemo(() => Array.from({ length: 7 }).map((_, i) => addDays(anchor, i)), [anchor]);
  const weekStart = toISO(anchor);
  const weekEnd = toISO(addDays(anchor, 6));

  // Holidays: index by date for fast lookup
  const holidaysByDate = useMemo(() => {
    const x = {};
    for (const h of holidays) {
      if (!h?.date) continue;
      if (x[h.date]) x[h.date] = { ...x[h.date], extra: [...(x[h.date].extra || []), h] };
      else x[h.date] = { ...h, extra: [] };
    }
    return x;
  }, [holidays]);

  // Vizibilitate proiecte/regii — planificatorul vede acum aceleași entry-uri ca adminul
  // (filtrul vechi pe categorii 5/6/6.1/7 a fost eliminat la cererea utilizatorului).
  // CUTOFF demontaj: ascundem proiectele cat 6.1 (sau cat 6 cu end_date trecut) finalizate înainte de cutoff,
  // ca să nu mai apară în matrix-ul de planificare pentru demontaj — utilizatorul a cerut curățare.
  const DISMANTLE_CUTOFF = "2026-05-01";
  // Categorii de ofertare/pipeline — nu sunt de executat, deci nu apar în Planificator
  // (decât dacă au deja alocări în schedule_entries, ca să nu ascundem planificări existente).
  const PIPELINE_CATS = ["1", "1.2", "2", "3", "4"];
  const visibleProjects = useMemo(() => {
    const plannedIds = new Set((data.entries || []).map((e) => e.project_id).filter(Boolean));
    return (projects || []).filter((p) => {
      const cat = String(p.category || "");
      if (PIPELINE_CATS.includes(cat) && !plannedIds.has(p.id)) return false;
      if (cat === "6.1") {
        const finRaw = p.finalization?.finalized_at || p.status_changed_at || p.start_date || "";
        const finDate = String(finRaw).slice(0, 10);
        if (finDate && finDate < DISMANTLE_CUTOFF) return false;
      }
      // Cat 6 cu end_date trecut < cutoff → la fel, nu mai e relevant pentru demontaj
      if (cat === "6" && p.end_date && p.end_date < DISMANTLE_CUTOFF) return false;
      return true;
    });
  }, [projects, data.entries]);
  const visibleRegii = useMemo(() => regii, [regii]);
  // Entries vizibile: pentru planificator afișăm doar cele legate de un proiect/regie existent
  // (entry-urile orfane rămân ascunse pentru a evita confuzie).
  const visibleProjectIds = useMemo(() => new Set(visibleProjects.map((p) => p.id)), [visibleProjects]);
  const visibleRegieIds = useMemo(() => new Set(visibleRegii.map((r) => r.id)), [visibleRegii]);
  const visibleEntries = useMemo(() => {
    if (!isPlanificator) return data.entries || [];
    return (data.entries || []).filter((e) => {
      if (e.project_id) return visibleProjectIds.has(e.project_id);
      if (e.regie_id) return visibleRegieIds.has(e.regie_id);
      return false;
    });
  }, [data.entries, isPlanificator, visibleProjectIds, visibleRegieIds]);

  const entriesForDay = (isoDay) =>
    visibleEntries.filter((e) => {
      if ((e.start_date || "") > isoDay || isoDay > (e.end_date || "")) return false;
      // Filtrare regie: dacă entry e legat de o regie cu interval finit, ascunde-l în afara intervalului
      if (e.regie_id) {
        const r = visibleRegii.find((x) => x.id === e.regie_id);
        if (r) {
          // Regie închisă (active=false) → nu afișa alocări fantomă rămase din trecut
          if (r.active === false) return false;
          const sd = r.start_date;
          const ed = r.indeterminate_end ? "9999-12-31" : (r.end_date || sd);
          if (sd && isoDay < sd) return false;
          if (ed && isoDay > ed) return false;
          if (isRegiePausedOn(r, isoDay)) return false;
        }
      }
      return true;
    });

  // Deduplicate entries pe (project_id|regie_id|label) per zi → un card / proiect-regie pe zi
  // Agregă person_ids și car_ids din toate entries-urile suprapuse, păstrând primul ID
  // ca referință de click (pentru editare)
  const dedupeEntriesForDay = (rawEntries) => {
    const byKey = new Map();
    for (const e of rawEntries) {
      const key = e.project_id ? `p:${e.project_id}` : (e.regie_id ? `r:${e.regie_id}` : `l:${(e.label || "").trim().toLowerCase()}`);
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, {
          ...e,
          person_ids: [...(e.person_ids || [])],
          car_ids: [...(e.car_ids || [])],
          _merged_ids: [e.id],
        });
      } else {
        for (const pid of (e.person_ids || [])) {
          if (!existing.person_ids.includes(pid)) existing.person_ids.push(pid);
        }
        for (const cid of (e.car_ids || [])) {
          if (!existing.car_ids.includes(cid)) existing.car_ids.push(cid);
        }
        existing._merged_ids.push(e.id);
        // Extinde intervalul afișat ca uniunea
        if ((e.start_date || "") < (existing.start_date || "")) existing.start_date = e.start_date;
        if ((e.end_date || "") > (existing.end_date || "")) existing.end_date = e.end_date;
      }
    }
    return Array.from(byKey.values());
  };

  // Calc disponibili pe o zi anume (pentru micro-summary deasupra fiecărei zile)
  const dayAvailability = (isoDay) => {
    const dayEntries = dedupeEntriesForDay(entriesForDay(isoDay));
    const allocatedPersonIds = new Set();
    const allocatedCarIds = new Set();
    for (const e of dayEntries) {
      for (const pid of (e.person_ids || [])) allocatedPersonIds.add(pid);
      for (const cid of (e.car_ids || [])) allocatedCarIds.add(cid);
    }
    const personsCount = (data.persons || []).filter((p) => {
      if (p.is_admin_user) return false;
      if (allocatedPersonIds.has(p.id)) return false;
      // Exclude persoanele deja concediate la sau înainte de această zi
      if (p.data_concedierii && p.data_concedierii <= isoDay) return false;
      const dayVac = (p.vacations || []).find((v) => (v.start_date || "") <= isoDay && isoDay <= (v.end_date || ""));
      if (dayVac) return false;
      return true;
    }).length;
    const carsCount = (data.cars || []).filter((c) => !allocatedCarIds.has(c.id) && (c.status !== "defect" || c.defect_drivable === true)).length;
    return { personsCount, carsCount };
  };

  // Persoanele LIBERE pe o zi: disponibile pentru muncă dar nealocate nicăieri
  const freePersonsForDay = (isoDay) => {
    const dayEntries = dedupeEntriesForDay(entriesForDay(isoDay));
    const allocatedPersonIds = new Set();
    for (const e of dayEntries) {
      for (const pid of (e.person_ids || [])) allocatedPersonIds.add(pid);
    }
    return (data.persons || []).filter((p) => {
      if (p.is_admin_user) return false;
      if (allocatedPersonIds.has(p.id)) return false;
      if (p.data_concedierii && p.data_concedierii <= isoDay) return false;
      const dayVac = (p.vacations || []).find((v) => (v.start_date || "") <= isoDay && isoDay <= (v.end_date || ""));
      if (dayVac) return false;
      return true;
    }).map((p) => p.name).sort((a, b) => a.localeCompare(b, "ro"));
  };

  // Helper centralizat: necesar default per regie pentru ziua iso (re-export din utils).
  // Vezi `regieDefaultMinForIso` în `planificator/utils.js` — cascade
  //   default_min_people_by_day[dow] || default_min_people (săptămânal).

  // Necesar (deficit) pe o zi: pentru proiecte/regii sub minimul de persoane/mașini
  // SÂMBĂTĂ: regiile sunt OPȚIONALE → nu contribuie la deficit.
  // DUMINICĂ / SĂRBĂTOARE NAȚIONALĂ: zi liberă → fără necesar.
  const isHolidayIso = (isoDay) => {
    const h = holidaysByDate[isoDay];
    return !!(h && h.type === "national");
  };
  // Helper: proiectul e în stare de DEMONTAJ pe ziua respectivă?
  // Demontajul se face după muncă / cu oameni liberi → NU intră în necesarul minim.
  // Condiții: cat 6.1 (Finalizat – De demontat), sau end_date trecut.
  // Un proiect e „de demontat" pe o singură zi — ziua imediat după finalizare/end_date.
  // (Cerut explicit de utilizator: nu vrea să rămână marcat „de demontat" zile la rând.)
  const isProjectInDismantle = (p, isoDay) => {
    if (!p) return false;
    const cat = String(p.category || "");
    // Calcul „dismantle day" din finalization sau end_date
    const finRaw = p.finalization?.finalized_at || p.status_changed_at || p.end_date || null;
    if (!finRaw) return false;
    let dismantleDay = null;
    try {
      const d = new Date(String(finRaw).slice(0, 10) + "T00:00:00");
      if (!isNaN(d.getTime())) {
        d.setDate(d.getDate() + 1);
        const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), dd = String(d.getDate()).padStart(2, "0");
        dismantleDay = `${y}-${m}-${dd}`;
      }
    } catch { /* fallthrough */ }
    if (!dismantleDay) return false;
    // cat 6.1: marcat manual ca finalizat → demontaj exact pe (finalization + 1)
    if (cat === "6.1") return dismantleDay === isoDay;
    // cat 6 cu end_date trecut → demontaj exact pe (end_date + 1)
    if (p.end_date && p.end_date < isoDay) return dismantleDay === isoDay;
    return false;
  };

  const dayDeficit = (isoDay) => {
    let personsNeeded = 0;
    let carsNeeded = 0;
    // Duminică sau sărbătoare națională → fără necesar
    const dt = new Date(isoDay + "T00:00:00");
    if (dt.getDay() === 0 || isHolidayIso(isoDay)) {
      return { personsNeeded, carsNeeded };
    }
    const regieWeekOverride = (regieId) => (data.regii_weeks || []).find((w) => w.regie_id === regieId);
    const sat = isSaturdayIso(isoDay);
    // SÂMBĂTA: planificare 100% liberă — fără reguli, fără deficit calculat
    if (sat) return { personsNeeded, carsNeeded };
    for (const e of dedupeEntriesForDay(entriesForDay(isoDay))) {
      let minP = 0, minC = 0;
      if (e.project_id) {
        const p = visibleProjects.find((x) => x.id === e.project_id);
        const inDismantle = p && isProjectInDismantle(p, isoDay);
        if (p && !inDismantle) {
          minP = Number(p.min_people || 0); minC = Number(p.min_cars || 0);
        }
        // Day override per project — IGNORAT pentru proiectele în demontaj
        // (demontajul nu mai are necesar — orice persoană alocată poate lucra în altă parte)
        if (!inDismantle) {
          const dov = (data.projects_day_overrides || []).find((o) => o.project_id === e.project_id && o.date === isoDay);
          if (dov && dov.min_people != null) minP = Number(dov.min_people);
        } else {
          minP = 0; minC = 0;
        }
      } else if (e.regie_id) {
        if (sat) continue; // sâmbătă: regii opționale
        const r = visibleRegii.find((x) => x.id === e.regie_id);
        // Dacă regia e în pauză pe ziua respectivă → fără deficit
        if (r && isRegiePausedOn(r, isoDay)) continue;
        // Dacă entry-ul depășește perioada activă a regiei (start/end) → fără deficit
        if (r) {
          const sd = r.start_date;
          const ed = r.indeterminate_end ? "9999-12-31" : (r.end_date || sd);
          if (sd && isoDay < sd) continue;
          if (ed && isoDay > ed) continue;
        }
        const ov = regieWeekOverride(e.regie_id);
        if (r) {
          minP = ov?.min_people != null ? Number(ov.min_people) : regieDefaultMinForIso(r, isoDay);
          minC = Number(r.default_min_cars || 0);
        }
        // Day override per regie
        const dov = (data.regii_day_overrides || []).find((o) => o.regie_id === e.regie_id && o.date === isoDay);
        if (dov && dov.min_people != null) minP = Number(dov.min_people);
      }
      const allocP = (e.person_ids || []).length;
      const allocC = (e.car_ids || []).length;
      if (allocP < minP) personsNeeded += (minP - allocP);
      if (allocC < minC) carsNeeded += (minC - allocC);
    }
    // Regii active fără entry (placeholders) — necesarul integral (cu override dacă există)
    if (!sat) {
      for (const r of regiiPlaceholdersForDay(isoDay)) {
        const ov = regieWeekOverride(r.id);
        let minP = ov?.min_people != null ? Number(ov.min_people) : regieDefaultMinForIso(r, isoDay);
        // Day override per regie
        const dov = (data.regii_day_overrides || []).find((o) => o.regie_id === r.id && o.date === isoDay);
        if (dov && dov.min_people != null) minP = Number(dov.min_people);
        personsNeeded += minP;
        carsNeeded += Number(r.default_min_cars || 0);
      }
    }
    return { personsNeeded, carsNeeded };
  };

  // Helper: regie este în pauză pe ziua isoDay? (definit ÎNAINTEA weeklyRegiePattern care îl folosește)
  const isRegiePausedOn = (r, isoDay) => {
    if (!r) return false;
    // Pauză full (boolean simplu — fără interval)
    if (r.paused === true && (!Array.isArray(r.pauses) || r.pauses.length === 0)) return true;
    // Pauze per interval (din /regii sau /planificator/week)
    const pauses = r.pauses || (data.regii_pauses || []).filter((p) => p.regie_id === r.id);
    for (const p of pauses) {
      const sd = p.start_date || "";
      const ed = p.end_date || "9999-12-31";
      if (sd && sd <= isoDay && isoDay <= ed) return true;
    }
    return false;
  };

  // Prioritate firme — ordinea cerută de utilizator. Restul firmelor în ordine alfabetică, după.
  const FIRMA_PRIORITY = ["implenia", "imas", "speztec", "klarotec", "maba"];
  const firmaPriorityRank = (f) => {
    const k = (f || "").trim().toLowerCase();
    const idx = FIRMA_PRIORITY.indexOf(k);
    return idx === -1 ? 999 : idx;
  };

  // Culoare stabilă per firmă — derivată din utils (FIRMA_COLOR_PALETTE).
  // Toate regiile aceleași firme primesc aceeași culoare, chiar dacă nu au persoane alocate.
  const firmaColor = _firmaColor;

  // Map firma → culoare propagată: dacă există măcar o regie cu culoare explicită,
  // folosește acea culoare pentru toate regiile aceleași firme. Altfel hash deterministic.
  const firmaColorMap = useMemo(() => {
    const map = new Map();
    for (const r of (regii || [])) {
      const f = (r.firma || "").trim().toLowerCase();
      if (!f || map.has(f)) continue;
      if (r.color) map.set(f, r.color);
    }
    return map;
  }, [regii]);

  // Culoare folosită pentru o regie (preferă r.color, apoi culoarea explicită a altei regii din firma,
  // apoi hash-color din firma, apoi amber default).
  const regieColor = (r) => {
    if (!r) return "#F59E0B";
    if (r.color) return r.color;
    const fk = (r.firma || "").trim().toLowerCase();
    if (fk && firmaColorMap.has(fk)) return firmaColorMap.get(fk);
    return _regieColor(r);
  };

  // Pentru fiecare regie din săpt., calculează:
  //   - firstNeedDow (1..6 unde Lu=1, Vi=5, Sâ=6) — prima zi LUCRĂTOARE (Lu→Sâ) la care cere persoane
  //   - needsFullWeek (true dacă cere Lu–Vi toate zilele)
  // Folosit pentru sortare: full-week întâi, apoi cele care încep marți, miercuri etc.
  const weeklyRegiePattern = useMemo(() => {
    const map = new Map();
    const weekDays = [];
    for (let i = 0; i < 6; i++) weekDays.push(toISO(addDays(anchor, i))); // Lu..Sâ
    for (const r of visibleRegii) {
      if (r.active === false) continue;
      const sd = r.start_date;
      const ed = r.indeterminate_end ? "9999-12-31" : (r.end_date || sd);
      let firstDow = 99;
      let workingDayCoverage = 0; // câte din Lu-Vi cere oameni
      for (let i = 0; i < weekDays.length; i++) {
        const iso = weekDays[i];
        if (sd && iso < sd) continue;
        if (ed && iso > ed) continue;
        if (isRegiePausedOn(r, iso)) continue;
        const dov = (data.regii_day_overrides || []).find((o) => o.regie_id === r.id && o.date === iso);
        const wkOv = (data.regii_weeks || []).find((w) => w.regie_id === r.id);
        const minP = dov && dov.min_people != null
          ? Number(dov.min_people)
          : (wkOv && wkOv.min_people != null ? Number(wkOv.min_people) : regieDefaultMinForIso(r, iso));
        if (minP > 0) {
          if (firstDow === 99) firstDow = i + 1; // Lu=1
          if (i < 5) workingDayCoverage += 1; // Lu-Vi
        }
      }
      map.set(r.id, { firstDow, needsFullWeek: workingDayCoverage === 5 });
    }
    return map;
  }, [anchor, visibleRegii, data.regii_day_overrides, data.regii_weeks, data.regii_pauses]);

  // Comparator stabil pentru regii: 1) firma priority, 2) display_order custom, 3) needsFullWeek desc, 4) firstDow asc, 5) name
  const compareRegii = (aRegieId, bRegieId, aLabel, bLabel) => {
    const ra = visibleRegii.find((x) => x.id === aRegieId);
    const rb = visibleRegii.find((x) => x.id === bRegieId);
    const fa = (ra?.firma || "").trim().toLowerCase() || "(fără firmă)";
    const fb = (rb?.firma || "").trim().toLowerCase() || "(fără firmă)";
    const pa = firmaPriorityRank(fa);
    const pb = firmaPriorityRank(fb);
    if (pa !== pb) return pa - pb;
    if (fa !== fb) return fa.localeCompare(fb);
    // În interiorul aceleiași firme: dacă userul a stabilit o ordine manuală (display_order),
    // folosim acea ordine. Lipsa câmpului = 1_000_000 (apare la final).
    const doA = (ra && typeof ra.display_order === "number") ? ra.display_order : 1_000_000;
    const doB = (rb && typeof rb.display_order === "number") ? rb.display_order : 1_000_000;
    if (doA !== doB) return doA - doB;
    const wa = weeklyRegiePattern.get(aRegieId) || { firstDow: 99, needsFullWeek: false };
    const wb = weeklyRegiePattern.get(bRegieId) || { firstDow: 99, needsFullWeek: false };
    if (wa.needsFullWeek !== wb.needsFullWeek) return wa.needsFullWeek ? -1 : 1;
    if (wa.firstDow !== wb.firstDow) return wa.firstDow - wb.firstDow;
    if (aRegieId !== bRegieId) return (aRegieId || "").localeCompare(bRegieId || "");
    return (aLabel || ra?.name || "").localeCompare(bLabel || rb?.name || "");
  };

  // Sortare entries pe zi: regii întâi (ordine săptămânală stabilă), apoi proiecte
  const sortDayEntries = (rawEntries) => {
    return [...rawEntries].sort((a, b) => {
      // 1) regii înainte de proiecte
      if (a.regie_id && !b.regie_id) return -1;
      if (!a.regie_id && b.regie_id) return 1;
      if (!a.regie_id && !b.regie_id) return (a.label || "").localeCompare(b.label || "");
      // 2) ambele regii — folosește comparatorul comun
      return compareRegii(a.regie_id, b.regie_id, a.label, b.label);
    });
  };

  // Sortare placeholders identică
  const sortRegiiPlaceholders = (placeholders) => {
    return [...placeholders].sort((a, b) => compareRegii(a.id, b.id, a.name || a.label, b.name || b.label));
  };

  // Helper: badge text + style pentru pattern-ul săptămânal al unei regii
  const regiePatternBadge = (regieId) => {
    const w = weeklyRegiePattern.get(regieId);
    if (!w || w.firstDow === 99) return null;
    if (w.needsFullWeek) return { label: "Lu–Vi", title: "Cere oameni toată săptămâna (Lu–Vi)", cls: "bg-blue-100 text-blue-800 border-blue-300" };
    const dowNames = { 1: "Lu", 2: "Ma", 3: "Mi", 4: "Jo", 5: "Vi", 6: "Sâ" };
    const d = dowNames[w.firstDow] || "?";
    return { label: `${d}→`, title: `Începe să ceară oameni de ${d}`, cls: "bg-violet-100 text-violet-800 border-violet-300" };
  };

  // Helper: regie active pe ziua respectivă fără schedule_entry — afișate ca placeholder
  const regiiPlaceholdersForDay = (isoDay) => {
    const isSat = isSaturdayIso(isoDay);
    return visibleRegii.filter((r) => {
      if (r.active === false) return false;
      if (isRegiePausedOn(r, isoDay)) return false;
      const sd = r.start_date;
      const ed = r.indeterminate_end ? "9999-12-31" : (r.end_date || sd);
      if (sd && isoDay < sd) return false;
      if (ed && isoDay > ed) return false;
      // (sâmbăta: tot afișăm cardul ca informativ — utilizatorul decide manual)
      if (!isSat) {
        const dov = (data.regii_day_overrides || []).find((o) => o.regie_id === r.id && o.date === isoDay);
        if (dov && dov.min_people === 0) return false;
      }
      const rName = (r.name || "").trim().toLowerCase();
      const has = visibleEntries.some((e) => {
        // Sâmbăta: doar entries SAT-specific (start_date === isoDay) blochează placeholder-ul
        if (isSat && e.start_date !== isoDay) return false;
        if ((e.start_date || "") > isoDay || isoDay > (e.end_date || "")) return false;
        // Match strict pe regie_id
        if (e.regie_id && e.regie_id === r.id) return true;
        // Fallback: match pe label (cazul entries orfane fără regie_id setat — bug istoric)
        // Astfel evităm afișarea dublă (placeholder + card real cu același nume)
        if (!e.regie_id && !e.project_id && rName && (e.label || "").trim().toLowerCase() === rName) return true;
        return false;
      });
      return !has;
    });
  };

  // Excludem din "Disponibili" persoanele cu admini și cu concediu activ în săptămâna curentă
  const { freePersons, busyPersons, onLeavePersons, freeCars } = useResourceSplit(data);

  // ====== Breakdown necesar per zi (pentru tooltip + comparație cu ziua anterioară) ======
  // Returnează { breakdown: [{key, firma, label, src, min, kind, ref_id}], totalP, totalC }
  const computeBreakdownForDay = (isoDay) => {
    const breakdown = [];
    let totalP = 0, totalC = 0;
    const isSat = isSaturdayIso(isoDay);
    const dayEntries = entriesForDay(isoDay);
    const regieWeekOverride = (rid) => (data.regii_weeks || []).find((w) => w.regie_id === rid);
    const regieDayOv = (rid) => (data.regii_day_overrides || []).find((o) => o.regie_id === rid && o.date === isoDay);
    const projectDayOv = (pid) => (data.projects_day_overrides || []).find((o) => o.project_id === pid && o.date === isoDay);
    for (const e of dayEntries) {
      if (e.project_id) {
        const p = visibleProjects.find((x) => x.id === e.project_id);
        if (p && !isProjectInDismantle(p, isoDay)) {
          const pdov = projectDayOv(e.project_id);
          const minP = pdov && pdov.min_people != null ? Number(pdov.min_people) : Number(p.min_people || 0);
          totalP += minP;
          totalC += Number(p.min_cars || 0);
          breakdown.push({ key: `p:${p.id}`, ref_id: p.id, firma: (p.auftraggeber || "").trim(), label: `📁 ${p.name || "—"}`, src: pdov ? "day_ov" : "proj_min", min: minP, kind: "project" });
        } else if (p && isProjectInDismantle(p, isoDay)) {
          breakdown.push({ key: `p:${p.id}`, ref_id: p.id, firma: (p.auftraggeber || "").trim(), label: `📁 ${p.name || "—"}`, src: "demontaj", min: 0, kind: "project" });
        } else if (!p) {
          breakdown.push({ key: `p:${e.project_id}`, ref_id: e.project_id, firma: "", label: `📁 ? (id=${e.project_id?.slice(0, 8)})`, src: "missing/hidden", min: 0, kind: "project" });
        }
      } else if (e.regie_id && !isSat) {
        const r = visibleRegii.find((x) => x.id === e.regie_id);
        const dov = regieDayOv(e.regie_id);
        const wkOv = regieWeekOverride(e.regie_id);
        if (r) {
          const minP = dov && dov.min_people != null
            ? Number(dov.min_people)
            : (wkOv && wkOv.min_people != null ? Number(wkOv.min_people) : regieDefaultMinForIso(r, isoDay));
          totalP += minP;
          totalC += Number(r.default_min_cars || 0);
          const src = dov ? "day_ov" : (wkOv ? "week_ov" : "default");
          breakdown.push({ key: `r:${r.id}`, ref_id: r.id, firma: (r.firma || "").trim(), label: `🟧 ${r.name || "—"} (entry)`, src, min: minP, kind: "regie" });
        } else {
          breakdown.push({ key: `r:${e.regie_id}`, ref_id: e.regie_id, firma: "", label: `🟧 ? (id=${e.regie_id?.slice(0, 8)}) entry`, src: "missing/hidden", min: 0, kind: "regie" });
        }
      } else if (e.regie_id && isSat) {
        const r = visibleRegii.find((x) => x.id === e.regie_id);
        breakdown.push({ key: `r:${e.regie_id}`, ref_id: e.regie_id, firma: (r?.firma || "").trim(), label: `🟧 ${r?.name || "—"} (sâmbătă opțional)`, src: "saturday_skip", min: 0, kind: "regie" });
      }
    }
    if (!isSat) {
      for (const r of regiiPlaceholdersForDay(isoDay)) {
        const dov = regieDayOv(r.id);
        const wkOv = regieWeekOverride(r.id);
        const minP = dov && dov.min_people != null
          ? Number(dov.min_people)
          : (wkOv && wkOv.min_people != null ? Number(wkOv.min_people) : regieDefaultMinForIso(r, isoDay));
        totalP += minP;
        totalC += Number(r.default_min_cars || 0);
        const src = dov ? "day_ov" : (wkOv ? "week_ov" : "default");
        breakdown.push({ key: `r:${r.id}`, ref_id: r.id, firma: (r.firma || "").trim(), label: `🟧 ${r.name || "—"} (placeholder)`, src, min: minP, kind: "regie" });
      }
    }
    breakdown.sort((a, b) => {
      const fa = (a.firma || "").toLowerCase();
      const fb = (b.firma || "").toLowerCase();
      if (!fa && fb) return 1;
      if (fa && !fb) return -1;
      if (fa !== fb) return fa.localeCompare(fb);
      return (a.label || "").localeCompare(b.label || "");
    });
    return { breakdown, totalP, totalC };
  };

  // Pre-calc breakdown pentru toate cele 7 zile ale săptămânii curente
  const weekBreakdowns = useMemo(() => {
    const map = {};
    for (const d of weekDays) {
      const iso = toISO(d);
      map[iso] = computeBreakdownForDay(iso);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDays, data, visibleProjects, visibleRegii]);

  return (
    <div className="space-y-5" data-testid="page-planificator">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Planificator</h1>
          <p className="text-sm text-zinc-500 mt-1">Calendar săptămânal · proiecte, regii, persoane și autoturisme</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <Button variant="outline" size="sm" onClick={() => setAnchor(addDays(anchor, -7))} data-testid="week-prev">
            <ChevronLeft size={16}/>
          </Button>
          <div
            className={`px-3 py-1.5 rounded-md bg-white border border-[var(--border)] text-sm font-semibold min-w-[180px] sm:min-w-[260px] text-center transition-all duration-300 flex-1 sm:flex-initial ${pulseWeekLabel ? "ring-4 ring-[var(--primary)]/60 scale-110 shadow-lg animate-pulse" : ""}`}
            data-testid="week-label"
          >
            {fmtRangeLabel(anchor, addDays(anchor, 6))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setAnchor(addDays(anchor, 7))} data-testid="week-next">
            <ChevronRight size={16}/>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setToolHistoryOpen(true)} data-testid="tool-history-btn" title="Istoric scule — pe ce șantiere au umblat și cu ce dube">
            🧰
          </Button>
          <Button size="sm" onClick={() => setAnchor(mondayOf(new Date()))} data-testid="week-azi">Azi</Button>
          <div className="inline-flex items-center rounded-md border border-zinc-300 overflow-hidden text-xs order-first sm:order-none w-full sm:w-auto" data-testid="view-mode-toggle">
            <button
              type="button"
              onClick={() => setViewMode("weekly")}
              className={`flex-1 sm:flex-initial px-2.5 py-1.5 font-semibold transition-colors ${viewMode === "weekly" ? "bg-[var(--primary)] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
              title="Vezi fiecare regie/proiect pe o singură linie orizontală (Lu → Du)"
              data-testid="view-weekly"
            >
              📊 Săptămânal
            </button>
            <button
              type="button"
              onClick={() => setViewMode("daily")}
              className={`flex-1 sm:flex-initial px-2.5 py-1.5 font-semibold transition-colors ${viewMode === "daily" ? "bg-[var(--primary)] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
              title="Vezi fiecare zi ca o coloană cu toate alocările"
              data-testid="view-daily"
            >
              🗓 Zilnic
            </button>
          </div>
          <AdminMaintenanceButtons userRole={user?.role} onChanged={load} />
        </div>
      </div>

      {/* MAGAZIA — To-Do List (proiect special intern) */}
      <MagaziaCard user={user} canEdit={canEditPlanning} onChanged={load} />

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5">
        {/* Week grid */}
        <div className={`card-flat p-2 min-w-0 ${dayColPreset.minPx > 0 ? "overflow-x-auto" : ""}`}>
          {viewMode === "weekly" ? (
            <WeeklyMatrix
              anchor={anchor}
              regii={[...visibleRegii].sort((a, b) => compareRegii(a.id, b.id, a.name, b.name))}
              projects={visibleProjects.filter((p) => {
                const ws = toISO(anchor);
                const we = toISO(addDays(anchor, 6));
                const sd = p.start_date || "";
                const ed = p.end_date || sd;
                return sd <= we && (ed === "" || ed >= ws);
              })}
              entries={visibleEntries}
              onCellClick={(isoDay) => setCreatingOn(isoDay)}
              onShareDay={(isoDay) => setSharing({ day: isoDay })}
              onTitleClick={(subject) => setHistoryRegie(subject)}
              isRegiePausedOn={isRegiePausedOn}
              isProjectInDismantle={isProjectInDismantle}
              holidaysByDate={holidaysByDate}
              regiiWeeks={data.regii_weeks || []}
              regiiDayOverrides={data.regii_day_overrides || []}
              projectsDayOverrides={data.projects_day_overrides || []}
            />
          ) : (
          <div className="overflow-x-auto -mx-2 sm:mx-0 pb-2 min-w-0" style={{ WebkitOverflowScrolling: "touch" }} data-testid="daily-scroll-container">
          <div
            className={`grid gap-2 min-h-[400px] px-2 sm:px-0`}
            style={{ gridTemplateColumns: `repeat(7, minmax(200px, 1fr))` }}
          >
            {weekDays.map((d) => {
              const isoDay = toISO(d);
              const entries = dedupeEntriesForDay(entriesForDay(isoDay));
              const isToday = isoDay === toISO(new Date());
              const isSundayReal = d.getDay() === 0; // duminica
              const isSatReal = d.getDay() === 6; // sâmbăta
              const hol = holidaysByDate[isoDay];
              const isNatHoliday = !!(hol && hol.type === "national");
              // Tratăm sărbătoarea națională identic cu duminica: zi liberă, fără proiecte/oameni alocați
              const isSunday = isSundayReal || isNatHoliday;
              // Pe duminică/sărbătoare/SÂMBĂTĂ, afișăm DOAR entries create EXPLICIT pe ziua respectivă (start_date === isoDay).
              // Sâmbăta: planificare 100% liberă, nu propagăm de pe luni-vineri.
              const effectiveEntries = (isSunday || isSatReal) ? entries.filter((e) => e.start_date === isoDay) : entries;
              return (
                <div
                  key={isoDay}
                  className={`rounded-md border ${isSunday ? "border-zinc-800 bg-zinc-900 text-zinc-300" : isToday ? "border-[var(--primary)] bg-[var(--primary-subtle)]" : "border-[var(--border)] bg-zinc-50"} p-2 flex flex-col gap-1.5 min-h-[320px]`}
                  data-testid={`day-col-${isoDay}`}
                  onContextMenu={(e) => {
                    if (entries.length === 0) return; // nimic de copiat
                    e.preventDefault();
                    setCopyingFrom(isoDay);
                  }}
                >
                  <div className={`px-1 pb-1 border-b ${isSunday ? "border-zinc-700" : "border-zinc-200"} flex flex-col gap-1`}>
                    {/* Row 1: day name + date | action buttons */}
                    <div className="flex items-center justify-between">
                      <div className={`text-xs font-bold whitespace-nowrap ${isSunday ? "text-zinc-200" : isToday ? "text-[var(--primary)]" : "text-zinc-700"}`}>{fmtRoDay(d)}</div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Buton „copiază alocările" eliminat la cererea adminului — funcția rămâne accesibilă prin click-dreapta pe coloana zilei (onContextMenu). */}
                        <button
                          onClick={() => setCreatingOn(isoDay)}
                          className={`${isSunday ? "text-zinc-300 hover:text-white hover:bg-zinc-700" : "text-[var(--primary)] hover:bg-white"} rounded-full w-5 h-5 flex items-center justify-center text-sm font-bold leading-none`}
                          title={isSunday ? "Alocă (zi liberă — folosește doar pentru excepții)" : "Alocă persoane/mașini pe această zi"}
                          data-testid={`add-day-${isoDay}`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    {/* Row 2: badges (only if any) */}
                    {(isNatHoliday || (isSundayReal && !isNatHoliday) || (isSatReal && !isSunday)) && (
                      <div className="flex items-center gap-1 flex-wrap">
                        {isNatHoliday && (
                          <span
                            className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-700 text-white border border-red-500 font-bold inline-flex items-center gap-1"
                            title={hol.name || "Sărbătoare națională"}
                            data-testid={`day-holiday-${isoDay}`}
                          >
                            🎉 {(hol.name || "Sărbătoare").length > 16 ? (hol.name || "Sărbătoare").slice(0, 14) + "…" : (hol.name || "Sărbătoare")}
                          </span>
                        )}
                        {isSundayReal && !isNatHoliday && (
                          <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-200 border border-zinc-600 font-bold" title="Duminică — zi liberă">ZI LIBERĂ</span>
                        )}
                        {isSatReal && !isSunday && (
                          <span
                            className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500 text-white border border-amber-600 font-bold"
                            title="Sâmbăta — zi normală de lucru: toate proiectele în curs și regiile apar cu 0 persoane, alocarea se face manual (fără propagare automată)"
                            data-testid={`day-saturday-${isoDay}`}
                          >
                            ⚡ SÂMBĂTĂ — ALOCARE MANUALĂ
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Daily availability summary: alocate/total disponibili + necesari rămași/total necesari */}
                  {!isSunday && (() => {
                    const av = dayAvailability(isoDay);
                    const def = dayDeficit(isoDay);
                    // Calculează alocările zilei (persoane/mașini unice alocate)
                    // Aplicăm aceleași reguli de eligibilitate ca modalul "+":
                    // persoane: exclude adminii și cei cu concediu/indisponibilitate în ziua respectivă
                    // mașini: exclude cele cu status "defect"
                    const dayEntries = entriesForDay(isoDay);
                    const allocPersonIds = new Set();
                    const allocCarIds = new Set();
                    for (const e of dayEntries) {
                      for (const pid of (e.person_ids || [])) allocPersonIds.add(pid);
                      for (const cid of (e.car_ids || [])) allocCarIds.add(cid);
                    }
                    const isEligiblePerson = (pid) => {
                      const p = (data.persons || []).find((x) => x.id === pid);
                      if (!p) return false;
                      if (p.is_admin_user) return false;
                      // Exclude persoanele deja concediate la sau înainte de această zi
                      if (p.data_concedierii && p.data_concedierii <= isoDay) return false;
                      const dayVac = (p.vacations || []).find((v) => (v.start_date || "") <= isoDay && isoDay <= (v.end_date || ""));
                      if (dayVac) return false;
                      return true;
                    };
                    const isEligibleCar = (cid) => {
                      const c = (data.cars || []).find((x) => x.id === cid);
                      if (!c) return false;
                      if (c.status === "defect" && c.defect_drivable !== true) return false;
                      return true;
                    };
                    const personsAllocated = [...allocPersonIds].filter(isEligiblePerson).length;
                    const carsAllocated = [...allocCarIds].filter(isEligibleCar).length;
                    const totalAvailablePersons = personsAllocated + av.personsCount;
                    const totalAvailableCars = carsAllocated + av.carsCount;
                    // Total necesari (suma min_people / min_cars din proiecte + regii + placeholders)
                    // SÂMBĂTĂ: regiile sunt opționale → nu contează la necesarul total.
                    // Day-level overrides (regii_day_overrides / projects_day_overrides) au prioritate
                    // peste week-level overrides și peste default.
                    // Folosim breakdown-ul pre-calculat (cu ref_id pentru comparație)
                    const { breakdown, totalP: totalPersonsRequired, totalC: totalCarsRequired } = weekBreakdowns[isoDay] || { breakdown: [], totalP: 0, totalC: 0 };
                    // Găsim ziua precedentă din săptămâna afișată pentru a marca diferențele
                    const wIdx = weekDays.findIndex((d) => toISO(d) === isoDay);
                    const prevIso = wIdx > 0 ? toISO(weekDays[wIdx - 1]) : null;
                    const prevBd = prevIso ? (weekBreakdowns[prevIso]?.breakdown || []) : [];
                    const prevByKey = new Map(prevBd.map((b) => [b.key, b.min]));
                    // Adnotăm fiecare linie cu prevMin/diff (doar dacă există ziua anterioară)
                    const annotated = breakdown.map((b) => {
                      if (!prevIso) return { ...b, diff: false, prevMin: null, isNew: false };
                      if (prevByKey.has(b.key)) {
                        const pv = prevByKey.get(b.key);
                        return { ...b, diff: pv !== b.min, prevMin: pv, isNew: false };
                      }
                      // Nu exista ieri → NU marcăm ca diff (cerința: doar diferență numerică între zile când există ambele)
                      return { ...b, diff: false, prevMin: null, isNew: false };
                    });
                    const anyDiff = annotated.some((b) => b.diff);
                    const hasDeficit = def.personsNeeded > 0 || def.carsNeeded > 0;
                    // Sumar "total disponibili / total necesari" + diferența — DOAR pentru persoane
                    // (mașinile sunt afișate separat sus și nu intră în calculul de balanță)
                    const totalAvailable = totalAvailablePersons;
                    const totalRequired = totalPersonsRequired;
                    const balance = totalAvailable - totalRequired;
                    const showSummary = totalRequired > 0 || totalAvailable > 0;
                    return (
                      <div
                        className="px-1 pb-1.5 flex items-center flex-wrap gap-1.5 text-[10px] font-semibold border-b border-zinc-200"
                        data-testid={`day-avail-${isoDay}`}
                      >
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${av.personsCount === 0 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}
                          title={`Alocate: ${personsAllocated} · Disponibile: ${av.personsCount} · Total disponibili: ${totalAvailablePersons}`}
                        >
                          <Users size={10}/> {personsAllocated}/{totalAvailablePersons} pers.
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${av.carsCount === 0 ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}
                          title={`Alocate: ${carsAllocated} · Disponibile: ${av.carsCount} · Total disponibile: ${totalAvailableCars}`}
                        >
                          <CarIcon size={10}/> {carsAllocated}/{totalAvailableCars} auto
                        </span>
                        {(totalPersonsRequired > 0 || totalCarsRequired > 0) && (
                          <span
                            className={`relative group inline-flex items-center gap-1 px-1.5 py-0.5 rounded border cursor-help ${hasDeficit ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-emerald-50 text-emerald-700 border-emerald-200"} ${anyDiff && prevIso ? "ring-2 ring-amber-400 ring-offset-1" : ""}`}
                            data-testid={`day-deficit-${isoDay}`}
                          >
                            <span className="uppercase tracking-wider text-[8px]">necesar</span>
                            {totalPersonsRequired > 0 && (
                              <span className="inline-flex items-center gap-0.5"><Users size={10}/>{def.personsNeeded}/{totalPersonsRequired}</span>
                            )}
                            {totalCarsRequired > 0 && (
                              <span className="inline-flex items-center gap-0.5"><CarIcon size={10}/>{def.carsNeeded}/{totalCarsRequired}</span>
                            )}
                            {anyDiff && prevIso && (
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" title="Diferențe față de ziua anterioară" />
                            )}
                            <NeedBreakdownTooltip
                              items={annotated}
                              prevIso={prevIso}
                              totalP={totalPersonsRequired}
                              isoDay={isoDay}
                            />
                          </span>
                        )}
                        {showSummary && (
                          <>
                            <span
                              className="relative group inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-300 w-full justify-center cursor-help"
                              data-testid={`day-totals-${isoDay}`}
                            >
                              <span className="uppercase tracking-wider text-[8px]">total pers.</span>
                              <span>{totalAvailable}/{totalRequired}</span>
                              <NeedBreakdownTooltip
                                items={annotated}
                                prevIso={prevIso}
                                totalP={totalPersonsRequired}
                                isoDay={isoDay}
                              />
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border w-full justify-center font-bold ${
                                balance >= 0
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                                  : "bg-red-50 text-red-700 border-red-300"
                              }`}
                              title="Diferența: total persoane disponibile minus total persoane necesare"
                              data-testid={`day-balance-${isoDay}`}
                            >
                              <span className="uppercase tracking-wider text-[8px]">diferență</span>
                              <span>{balance >= 0 ? `+${balance}` : `${balance}`}</span>
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })()}
                  {isSunday ? (
                    <div className="text-[11px] text-zinc-400 italic px-1 pt-2 text-center">
                      {isNatHoliday ? <>🎉 <strong className="text-red-300">{hol.name}</strong><br/></> : null}
                      Liber. Apasă <strong className="text-zinc-200">+</strong> doar pentru excepții.
                    </div>
                  ) : effectiveEntries.length === 0 ? (
                    <div className="text-[11px] text-zinc-400 italic px-1 pt-2">—</div>
                  ) : (
                    (() => {
                      const sorted = sortDayEntries(effectiveEntries);
                      const items = [];
                      let lastFirma = "__INIT__";
                      let injectedProjectsHeader = false;
                      const overrideRegieIds = new Set((data.regii_weeks || []).map((w) => w.regie_id));
                      const firmaOf = (rid) => {
                        if (!rid) return null;
                        const r = visibleRegii.find((x) => x.id === rid);
                        return (r?.firma || "").trim() || "Fără firmă";
                      };
                      // 🟢 Placeholdere (regii fără entry) grupate pe firma → vor fi injectate inline sub firma corespunzătoare
                      const allPlaceholders = !isSunday ? sortRegiiPlaceholders(regiiPlaceholdersForDay(isoDay)) : [];
                      const placeholdersByFirma = {};
                      for (const r of allPlaceholders) {
                        const f = (r.firma || "").trim() || "Fără firmă";
                        if (!placeholdersByFirma[f]) placeholdersByFirma[f] = [];
                        placeholdersByFirma[f].push(r);
                      }
                      const renderedFirmas = new Set();
                      // Render-placeholder helper: produce JSX for a single placeholder regie (mută din blocul standalone)
                      const renderPlaceholder = (r) => {
                        const minP = regieDefaultMinForIso(r, isoDay);
                        const dayOv = (data.regii_day_overrides || {})[r.id]?.[isoDay];
                        const isOv = dayOv?.min != null;
                        const isSat = isSaturdayIso(isoDay);
                        const firma = (r.firma || "").trim() || "Fără firmă";
                        const isDragging = draggingRegieId === r.id;
                        const isDragOver = dragOverRegieId === r.id && draggingRegieId && draggingRegieId !== r.id;
                        return (
                          <div
                            key={`placeholder-${r.id}-${isoDay}`}
                            role="button"
                            tabIndex={0}
                            draggable={canEditPlanning}
                            onDragStart={(ev) => {
                              if (!canEditPlanning) return;
                              setDraggingRegieId(r.id);
                              try { ev.dataTransfer.setData("text/plain", JSON.stringify({ rid: r.id, firma })); ev.dataTransfer.effectAllowed = "move"; } catch { /* noop */ }
                            }}
                            onDragEnd={() => { setDraggingRegieId(null); setDragOverRegieId(null); }}
                            onDragOver={(ev) => {
                              if (!draggingRegieId || draggingRegieId === r.id) return;
                              const srcR = visibleRegii.find((x) => x.id === draggingRegieId);
                              const srcFirma = (srcR?.firma || "").trim() || "Fără firmă";
                              if (srcFirma !== firma) return;
                              ev.preventDefault();
                              ev.dataTransfer.dropEffect = "move";
                              setDragOverRegieId(r.id);
                            }}
                            onDragLeave={() => { if (dragOverRegieId === r.id) setDragOverRegieId(null); }}
                            onDrop={(ev) => {
                              ev.preventDefault();
                              const src = draggingRegieId;
                              setDraggingRegieId(null);
                              setDragOverRegieId(null);
                              if (!src || src === r.id) return;
                              reorderRegiiInFirma(firma, src, r.id);
                            }}
                            onClick={() => setCreatingOn(isoDay)}
                            onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); setCreatingOn(isoDay); } }}
                            className={`text-left rounded px-2 py-1.5 text-[11px] border-2 transition-colors cursor-pointer hover:shadow-sm ${isSat ? "opacity-75" : ""} ${isDragging ? "opacity-40" : ""} ${isDragOver ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}
                            style={{ borderColor: regieColor(r), color: "white", background: regieColor(r) }}
                            data-testid={`regie-placeholder-${r.id}-${isoDay}`}
                            title={isSat ? "Regie opțională sâmbăta — click pentru a aloca dacă lucrezi" : (canEditPlanning ? "Click pentru alocare · trage pentru a reordona" : "Regie activă — click pentru a aloca personal")}
                          >
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-white/30 text-white flex-shrink-0">regie</span>
                              {isSat && <span className="text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-white/30 text-white flex-shrink-0" title="Opțional sâmbăta">opt</span>}
                              <div className="ml-auto flex items-center gap-0.5 flex-shrink-0">
                                {canEditPlanning && (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); setEditingRegieId(r.id); }}
                                    onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.stopPropagation(); ev.preventDefault(); setEditingRegieId(r.id); } }}
                                    className="inline-flex items-center justify-center w-5 h-5 rounded text-[11px] hover:bg-white/30 cursor-pointer text-white"
                                    title="Editează toate detaliile regiei (firma, perioada, necesarul de persoane etc.)"
                                    data-testid={`placeholder-edit-full-${r.id}-${isoDay}`}
                                  >
                                    ✏️
                                  </span>
                                )}
                                {canEditPlanning && (() => {
                                  const dismantleSubj = r;
                                  let currentSD = dismantleSubj?.start_date || "";
                                  let currentED = dismantleSubj?.end_date || "";
                                  const currentIndet = !!dismantleSubj?.indeterminate_end;
                                  if (!currentSD || (!currentED && !currentIndet)) {
                                    const related = visibleEntries.filter((x) => x.regie_id === r.id);
                                    if (!currentSD) {
                                      const mins = related.map((x) => x.start_date).filter(Boolean).sort();
                                      if (mins.length) currentSD = mins[0];
                                    }
                                    if (!currentED && !currentIndet) {
                                      const maxs = related.map((x) => x.end_date).filter(Boolean).sort();
                                      if (maxs.length) currentED = maxs[maxs.length - 1];
                                    }
                                  }
                                  return (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); setPostponing({ type: "regie", ref_id: r.id, key: `placeholder-${r.id}`, label: r.name || "—", currentStartDate: currentSD, currentEndDate: currentED, currentIndeterminateEnd: currentIndet }); }}
                                      onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.stopPropagation(); ev.preventDefault(); ev.currentTarget.click(); } }}
                                      className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] hover:bg-white/30 cursor-pointer text-white"
                                      title="Amână (mută data de început)"
                                    >
                                      📅
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>
                            <div className="font-semibold text-[12px] leading-snug line-clamp-2 break-words mt-1" title={r.name || "—"}>{r.name || "—"}</div>
                            <div className="text-[9px] opacity-90 mt-1 italic flex items-center gap-1 flex-wrap">
                              <span className="flex-1">{isSat ? "click → alocă manual · 0/0" : `click → alocă · necesar ${minP} pers${isOv ? " ★" : ""}`}</span>
                              {!isSat && canEditPlanning && (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(ev) => { ev.stopPropagation(); setEditingMinPeople({ target: { kind: "regie", id: r.id, name: r.name || "—" }, isoDay, currentMin: minP, currentMax: Number(r.default_max_people || 0) }); }}
                                  onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); ev.currentTarget.click(); } }}
                                  className="ml-0.5 px-1 rounded hover:bg-white/30 cursor-pointer not-italic font-bold text-white"
                                  title="Modifică necesarul (0 = nu mai cere persoane)"
                                  data-testid={`placeholder-${r.id}-${isoDay}-edit-min`}
                                >
                                  ✎
                                </span>
                              )}
                              {r.vorarbeiter && (
                                <span className="ml-0.5 px-1 rounded bg-white/30 text-white text-[9px] truncate max-w-[80px]" title={r.vorarbeiter}>
                                  👷 {r.vorarbeiter}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      };
                      const flushPlaceholdersFor = (firma) => {
                        if (!firma || renderedFirmas.has(firma)) return;
                        const arr = placeholdersByFirma[firma] || [];
                        if (arr.length === 0) { renderedFirmas.add(firma); return; }
                        for (const r of arr) items.push(renderPlaceholder(r));
                        renderedFirmas.add(firma);
                      };
                      const overrideCountForFirma = (firma) => {
                        const seen = new Set();
                        for (const x of sorted) {
                          if (x.regie_id && firmaOf(x.regie_id) === firma && overrideRegieIds.has(x.regie_id)) seen.add(x.regie_id);
                        }
                        for (const r of (placeholdersByFirma[firma] || [])) {
                          if (overrideRegieIds.has(r.id)) seen.add(r.id);
                        }
                        return seen.size;
                      };
                      for (const e of sorted) {
                        // Tranziție regii → proiecte: inserează separator vizual
                        if (!e.regie_id && !e.project_id) {
                          // entry orfan — nu inserăm separator
                        } else if (!e.regie_id && !injectedProjectsHeader) {
                          // 🟢 Înainte să trecem la proiecte, emitem placeholderii pentru firma curentă
                          // și pentru orice firmă cu placeholderi care nu a apărut încă (regii fără entries azi)
                          if (lastFirma && lastFirma !== "__INIT__") flushPlaceholdersFor(lastFirma);
                          for (const fname of Object.keys(placeholdersByFirma)) {
                            if (!renderedFirmas.has(fname)) {
                              const ovCount = (placeholdersByFirma[fname] || []).filter((r) => overrideRegieIds.has(r.id)).length;
                              items.push(
                                <div
                                  key={`ph-hdr-only-${isoDay}-${fname}`}
                                  className="flex items-center gap-1 text-[8px] uppercase tracking-widest font-bold text-zinc-500 px-1 pt-1 pb-0.5"
                                >
                                  <span>{fname}</span>
                                  {ovCount > 0 && (<span className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-amber-200 text-amber-900 text-[8px] font-bold normal-case tracking-normal" title={`${ovCount} ${ovCount === 1 ? "regie are" : "regii au"} necesar diferit săptămâna asta`}>●{ovCount}</span>)}
                                </div>
                              );
                              flushPlaceholdersFor(fname);
                            }
                          }
                          injectedProjectsHeader = true;
                          items.push(
                            <div
                              key={`hdr-projects-${isoDay}`}
                              className="flex items-center gap-1 text-[8px] uppercase tracking-widest font-bold text-blue-700 px-1 pt-2 pb-0.5 border-t border-blue-200/60 mt-1"
                              data-testid={`projects-separator-${isoDay}`}
                            >
                              <span>📁 Proiecte</span>
                            </div>
                          );
                        }
                        const firma = firmaOf(e.regie_id);
                        if (firma !== lastFirma) {
                          // 🟢 Înainte de a trece la firma nouă, golim placeholderii rămași pentru firma curentă
                          if (lastFirma && lastFirma !== "__INIT__") flushPlaceholdersFor(lastFirma);
                          if (firma) {
                            const ovCount = overrideCountForFirma(firma);
                            items.push(
                              <div
                                key={`hdr-${isoDay}-${firma}`}
                                className="flex items-center gap-1 text-[8px] uppercase tracking-widest font-bold text-zinc-500 px-1 pt-1 pb-0.5 first:pt-0"
                              >
                                <span>{firma}</span>
                                {ovCount > 0 && (
                                  <span
                                    className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-amber-200 text-amber-900 text-[8px] font-bold normal-case tracking-normal"
                                    title={`${ovCount} ${ovCount === 1 ? "regie are" : "regii au"} necesar diferit săptămâna asta`}
                                    data-testid={`firma-override-${isoDay}-${firma}`}
                                  >
                                    ●{ovCount}
                                  </span>
                                )}
                              </div>
                            );
                          }
                          lastFirma = firma;
                        }
                        items.push((() => {
                          // Pentru entries de regie fără culoare, folosește firmaColor() pentru consistență vizuală
                          let entryColor = e.color;
                          if (!entryColor && e.regie_id) {
                            const r = visibleRegii.find((x) => x.id === e.regie_id);
                            if (r) entryColor = regieColor(r);
                          }
                          entryColor = entryColor || "#3B82F6";
                          // 🎨 Set de scule alocat → culoarea setului are prioritate vizuală (max 2 culori → gradient)
                          let entryBg = entryColor;
                          const eToolCols = (e.allocation_colors && e.allocation_colors.length) ? e.allocation_colors : (e.allocation_color ? [e.allocation_color] : []);
                          if (eToolCols.length && TOOL_SET_HEX[eToolCols[0]]) {
                            entryColor = TOOL_SET_HEX[eToolCols[0]];
                            entryBg = eToolCols.length >= 2
                              ? `linear-gradient(135deg, ${TOOL_SET_HEX[eToolCols[0]]} 0%, ${TOOL_SET_HEX[eToolCols[0]]} 50%, ${TOOL_SET_HEX[eToolCols[1]]} 50%, ${TOOL_SET_HEX[eToolCols[1]]} 100%)`
                              : TOOL_SET_HEX[eToolCols[0]];
                          } else {
                            entryBg = entryColor;
                          }
                          const isRegieEntry = !!e.regie_id;
                          const entryFirma = isRegieEntry ? (firmaOf(e.regie_id) || "Fără firmă") : null;
                          const isDragging = isRegieEntry && draggingRegieId === e.regie_id;
                          const isDragOver = isRegieEntry && dragOverRegieId === e.regie_id && draggingRegieId && draggingRegieId !== e.regie_id;
                          return (
                          <div
                            key={e.id}
                            role="button"
                            tabIndex={0}
                            draggable={isRegieEntry && canEditPlanning}
                            onDragStart={(ev) => {
                              if (!isRegieEntry || !canEditPlanning) return;
                              setDraggingRegieId(e.regie_id);
                              try { ev.dataTransfer.setData("text/plain", JSON.stringify({ rid: e.regie_id, firma: entryFirma })); ev.dataTransfer.effectAllowed = "move"; } catch { /* noop */ }
                            }}
                            onDragEnd={() => { setDraggingRegieId(null); setDragOverRegieId(null); }}
                            onDragOver={(ev) => {
                              if (!isRegieEntry || !draggingRegieId || draggingRegieId === e.regie_id) return;
                              const srcR = visibleRegii.find((x) => x.id === draggingRegieId);
                              const srcFirma = (srcR?.firma || "").trim() || "Fără firmă";
                              if (srcFirma !== entryFirma) return;
                              ev.preventDefault();
                              ev.dataTransfer.dropEffect = "move";
                              setDragOverRegieId(e.regie_id);
                            }}
                            onDragLeave={() => { if (dragOverRegieId === e.regie_id) setDragOverRegieId(null); }}
                            onDrop={(ev) => {
                              if (!isRegieEntry) return;
                              ev.preventDefault();
                              const src = draggingRegieId;
                              setDraggingRegieId(null);
                              setDragOverRegieId(null);
                              if (!src || src === e.regie_id) return;
                              reorderRegiiInFirma(entryFirma, src, e.regie_id);
                            }}
                            onClick={() => setEditing({ ...e, _clickedDay: isoDay })}
                            onKeyDown={(ev) => {
                              if (ev.key === "Enter" || ev.key === " ") {
                                ev.preventDefault();
                                setEditing({ ...e, _clickedDay: isoDay });
                              }
                            }}
                            className={`text-left rounded px-2 py-1.5 text-xs hover:shadow-sm transition-shadow border cursor-pointer ${e.regie_id && isSaturdayIso(isoDay) ? "opacity-75" : ""} ${isDragging ? "opacity-40" : ""} ${isDragOver ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}
                            style={{
                              background: entryBg,
                              borderColor: entryColor,
                              color: eToolCols.includes("galben") ? "#422006" : "white",
                            }}
                            data-testid={`entry-${e.id}-${isoDay}`}
                            title={e.regie_id && isSaturdayIso(isoDay) ? "Regie opțională sâmbăta — nu contează la NECESAR" : (isRegieEntry && canEditPlanning ? "Click pentru editare · trage pentru a reordona" : undefined)}
                          >
                            <div className="font-semibold whitespace-normal break-words leading-tight flex items-center gap-1">
                              {e.regie_id && isSaturdayIso(isoDay) && (
                                <span className="inline-block text-[8px] uppercase tracking-wider px-1 py-0 rounded bg-white/30 text-white font-bold flex-shrink-0" title="Sâmbătă — regie opțională">opt</span>
                              )}
                              {e.project_id && (() => {
                                const proj = visibleProjects.find((x) => x.id === e.project_id);
                                return proj && isProjectInDismantle(proj, isoDay);
                              })() && (
                                <span
                                  className="inline-block text-[8px] uppercase tracking-wider px-1 py-0 rounded bg-orange-300 text-orange-900 font-extrabold flex-shrink-0"
                                  title="Proiect în stare de demontaj — nu cere personal minim (demontajul se face după muncă / cu oameni liberi)"
                                  data-testid={`entry-dismantle-${e.id}-${isoDay}`}
                                >
                                  🔧 DEMONTAJ
                                </span>
                              )}
                              {e._merged_ids && e._merged_ids.length > 1 && (
                                <span
                                  className="inline-block text-[8px] uppercase tracking-wider px-1 py-0 rounded bg-amber-300 text-amber-900 font-extrabold flex-shrink-0 animate-pulse"
                                  title={`Acest card conține ${e._merged_ids.length} alocări duplicate suprapuse. Click pe „🧹 Curăță duplicate" sus pentru a le uni definitiv.`}
                                  data-testid={`entry-merged-${e.id}-${isoDay}`}
                                >
                                  ⚠ {e._merged_ids.length}×
                                </span>
                              )}
                              <span className="flex-1">{e.label}</span>
                              {(() => {
                                // Ceas: verde = toate persoanele au ore logate în ziua asta → gata de facturat
                                //        portocaliu = doar unele au logat → parțial
                                //        roșu      = niciuna nu a logat → lipsesc orele
                                const st = (e.hours_status_by_date || {})[isoDay];
                                if (!st || st === "no_persons") return null;
                                const cfg = {
                                  complete: { bg: "bg-green-500", icon: "🕐", title: "Toate orele completate pentru toți alocații — gata de facturat" },
                                  partial:  { bg: "bg-orange-400", icon: "🕐", title: "Doar unele persoane au logat orele — incomplet" },
                                  missing:  { bg: "bg-red-500",    icon: "🕐", title: "Nimeni nu a logat orele încă — LIPSESC" },
                                }[st];
                                if (!cfg) return null;
                                return (
                                  <span
                                    className={`flex-shrink-0 inline-flex items-center justify-center rounded-full ${cfg.bg} text-white text-[9px] w-4 h-4 leading-none shadow-sm`}
                                    title={cfg.title}
                                    data-testid={`entry-hours-status-${st}-${e.id}-${isoDay}`}
                                  >
                                    {cfg.icon}
                                  </span>
                                );
                              })()}
                              {e.allocation_color && (() => {
                                const COLOR_EMOJI = { rosu: "🔴", galben: "🟡", albastru: "🔵", verde: "🟢", gri: "⚪", mov: "🟣" };
                                const COLOR_LABEL = { rosu: "Roșu", galben: "Galben", albastru: "Albastru", verde: "Verde", gri: "Gri", mov: "Mov" };
                                const emoji = COLOR_EMOJI[e.allocation_color];
                                if (!emoji) return null;
                                return (
                                  <span
                                    className="flex-shrink-0 text-[12px]"
                                    title={`Set scule atribuit: ${COLOR_LABEL[e.allocation_color]}`}
                                    data-testid={`entry-alloc-color-${e.id}`}
                                  >
                                    {emoji}
                                  </span>
                                );
                              })()}
                              {canEditPlanning && (e.regie_id || e.project_id) && (() => {
                                const subj = e.regie_id
                                  ? visibleRegii.find((x) => x.id === e.regie_id)
                                  : visibleProjects.find((x) => x.id === e.project_id);
                                // 🟢 Fallback: dacă regia/proiectul nu are start_date salvat în BD,
                                // folosește cea mai veche dată a alocării existente (entries pe acel ref_id)
                                let currentSD = subj?.start_date || "";
                                let currentED = subj?.end_date || "";
                                const currentIndet = !!subj?.indeterminate_end;
                                if (!currentSD || !currentED) {
                                  const refKey = e.regie_id ? "regie_id" : "project_id";
                                  const refId = e.regie_id || e.project_id;
                                  const relatedEntries = visibleEntries.filter((x) => x[refKey] === refId);
                                  if (!currentSD) {
                                    const mins = relatedEntries.map((x) => x.start_date).filter(Boolean).sort();
                                    if (mins.length > 0) currentSD = mins[0];
                                  }
                                  if (!currentED && !currentIndet) {
                                    const maxs = relatedEntries.map((x) => x.end_date).filter(Boolean).sort();
                                    if (maxs.length > 0) currentED = maxs[maxs.length - 1];
                                  }
                                }
                                return (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      ev.preventDefault();
                                      setPostponing({
                                        type: e.regie_id ? "regie" : "project",
                                        ref_id: e.regie_id || e.project_id,
                                        key: e.id,
                                        label: e.label,
                                        currentStartDate: currentSD,
                                        currentEndDate: currentED,
                                        currentIndeterminateEnd: currentIndet,
                                      });
                                    }}
                                    onKeyDown={(ev) => {
                                      if (ev.key === "Enter" || ev.key === " ") {
                                        ev.stopPropagation();
                                        ev.preventDefault();
                                        ev.currentTarget.click();
                                      }
                                    }}
                                    className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded text-[10px] bg-white/30 hover:bg-white/60 hover:text-zinc-900 cursor-pointer transition-colors"
                                    title="Amână (mută data de început)"
                                    data-testid={`entry-postpone-${e.id}-${isoDay}`}
                                  >
                                    📅
                                  </span>
                                );
                              })()}
                            </div>
                            <div className="flex items-center gap-2 text-[10px] opacity-90 mt-0.5">
                              {(() => {
                                let minP = 0, minC = 0;
                                let minPSource = "default"; // default | day_override
                                // Verificăm întâi dacă proiectul e în demontaj — atunci nu mai avem necesar
                                const projForDismantle = e.project_id ? visibleProjects.find((x) => x.id === e.project_id) : null;
                                const inDismantle = !!(projForDismantle && isProjectInDismantle(projForDismantle, isoDay));
                                // Day override (per-zi) are prioritate maximă — DAR nu pentru proiectele în demontaj
                                const dayOv = inDismantle
                                  ? null
                                  : (e.regie_id
                                    ? (data.regii_day_overrides || []).find((o) => o.regie_id === e.regie_id && o.date === isoDay)
                                    : (e.project_id ? (data.projects_day_overrides || []).find((o) => o.project_id === e.project_id && o.date === isoDay) : null));
                                if (e.project_id) {
                                  if (!inDismantle && projForDismantle) {
                                    minP = Number(projForDismantle.min_people || 0); minC = Number(projForDismantle.min_cars || 0);
                                  }
                                } else if (e.regie_id) {
                                  const reg = visibleRegii.find((x) => x.id === e.regie_id);
                                  const ov = (data.regii_weeks || []).find((w) => w.regie_id === e.regie_id);
                                  if (reg) {
                                    minP = ov?.min_people != null ? Number(ov.min_people) : regieDefaultMinForIso(reg, isoDay);
                                    minC = Number(reg.default_min_cars || 0);
                                  }
                                }
                                if (dayOv && dayOv.min_people != null) {
                                  minP = Number(dayOv.min_people);
                                  minPSource = "day_override";
                                }
                                const allocP = (e.person_ids || []).length;
                                const allocC = (e.car_ids || []).length;
                                // Sâmbătă: regiile sunt opționale → fără highlight roșu de "understaffed"
                                const isOptional = e.regie_id && isSaturdayIso(isoDay);
                                const personUnder = !isOptional && minP > 0 && allocP < minP;
                                const carUnder = !isOptional && minC > 0 && allocC < minC;
                                return (
                                  <>
                                    {(e.project_id || e.regie_id) && canEditPlanning ? (
                                      <button
                                        type="button"
                                        onClick={(ev) => {
                                          ev.stopPropagation();
                                          // Determină nume + max curent pentru pre-populare dialog
                                          let name = e.label || "—";
                                          let curMax = 0;
                                          if (e.regie_id) {
                                            const r = (data.regii || []).find((x) => x.id === e.regie_id);
                                            if (r) { name = r.name || name; curMax = Number(r.default_max_people || 0); }
                                          } else if (e.project_id) {
                                            const p = (data.projects || []).find((x) => x.id === e.project_id);
                                            if (p) { name = p.name || name; curMax = Number(p.max_people || 0); }
                                          }
                                          setEditingMinPeople({
                                            target: { kind: e.regie_id ? "regie" : "project", id: e.regie_id || e.project_id, name },
                                            isoDay,
                                            currentMin: minP,
                                            currentMax: curMax,
                                          });
                                        }}
                                        className={`inline-flex items-center gap-0.5 cursor-pointer hover:bg-white/30 px-1 rounded ${personUnder ? "bg-red-500/40 font-bold" : ""} ${minPSource === "day_override" ? "ring-1 ring-amber-300" : ""}`}
                                        title={`Click pentru a modifica necesarul de persoane pentru ${isoDay}${minPSource === "day_override" ? ` (override activ: ${minP} pers)` : ""}`}
                                        data-testid={`entry-${e.id}-${isoDay}-edit-min`}
                                      >
                                        <Users size={10}/>
                                        <span className="font-semibold">{allocP}{minP > 0 ? `/${minP}` : ""}</span>
                                        {minPSource === "day_override" && <span className="text-[9px]">★</span>}
                                        <span className="text-[9px] opacity-70 ml-0.5">✎</span>
                                      </button>
                                    ) : (
                                      <span
                                        className={`inline-flex items-center gap-0.5 ${personUnder ? "px-1 rounded bg-red-500/40 font-bold" : ""} ${minPSource === "day_override" ? "ring-1 ring-amber-300 rounded px-1" : ""}`}
                                        title={minP > 0 ? `Alocați ${allocP} din minim ${minP}` : `${allocP} persoane alocate`}
                                      >
                                        <Users size={10}/>{allocP}{minP > 0 ? `/${minP}` : ""}
                                        {minPSource === "day_override" && <span className="text-[8px]">★</span>}
                                      </span>
                                    )}
                                    <span
                                      className={`inline-flex items-center gap-0.5 ${carUnder ? "px-1 rounded bg-red-500/40 font-bold" : ""}`}
                                      title={minC > 0 ? `Alocate ${allocC} din minim ${minC}` : `${allocC} mașini alocate`}
                                    >
                                      <CarIcon size={10}/>{allocC}{minC > 0 ? `/${minC}` : ""}
                                    </span>
                                  </>
                                );
                              })()}
                              {e.is_regie && <span className="px-1 rounded bg-white/30 text-[9px] uppercase tracking-wider">regie</span>}
                              {e.regie_id && (() => {
                                const b = regiePatternBadge(e.regie_id);
                                if (!b) return null;
                                return (
                                  <span
                                    className="px-1 rounded bg-white/40 text-[9px] font-bold tracking-wide"
                                    title={b.title}
                                    data-testid={`entry-pattern-${e.id}-${isoDay}`}
                                  >
                                    {b.label}
                                  </span>
                                );
                              })()}
                              {e.regie_id && overrideRegieIds.has(e.regie_id) && (
                                <span className="inline-flex items-center px-1 rounded bg-amber-200 text-amber-900 text-[9px] font-bold" title="Necesar săptămână override">●</span>
                              )}
                            </div>
                            {e.note && (
                              <div
                                className="mt-1 text-[10px] leading-tight bg-white/30 rounded px-1.5 py-0.5 italic font-medium"
                                title={e.note}
                                data-testid={`entry-note-${e.id}-${isoDay}`}
                              >
                                📝 {e.note}
                              </div>
                            )}
                            {(e.regie_id || e.project_id) && (() => {
                              const item = e.regie_id
                                ? visibleRegii.find((x) => x.id === e.regie_id)
                                : visibleProjects.find((x) => x.id === e.project_id);
                              if (!item?.vorarbeiter) return null;
                              return (
                                <div
                                  className="mt-1 text-[10px] leading-tight opacity-95 italic flex items-center gap-1"
                                  title={`Vorarbeiter: ${item.vorarbeiter}`}
                                  data-testid={`entry-vorarbeiter-${e.id}-${isoDay}`}
                                >
                                  <span>👷</span>
                                  <span className="truncate">{item.vorarbeiter}</span>
                                </div>
                              );
                            })()}
                          </div>
                          );
                        })());
                      }
                      // 🟢 După ultimul entry: emite placeholderii pentru firma curentă și firmele rămase
                      if (lastFirma && lastFirma !== "__INIT__") flushPlaceholdersFor(lastFirma);
                      for (const fname of Object.keys(placeholdersByFirma)) {
                        if (!renderedFirmas.has(fname)) {
                          const ovCount = (placeholdersByFirma[fname] || []).filter((r) => overrideRegieIds.has(r.id)).length;
                          items.push(
                            <div
                              key={`ph-hdr-tail-${isoDay}-${fname}`}
                              className="flex items-center gap-1 text-[8px] uppercase tracking-widest font-bold text-zinc-500 px-1 pt-1 pb-0.5"
                            >
                              <span>{fname}</span>
                              {ovCount > 0 && (<span className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-amber-200 text-amber-900 text-[8px] font-bold normal-case tracking-normal" title={`${ovCount} ${ovCount === 1 ? "regie are" : "regii au"} necesar diferit săptămâna asta`}>●{ovCount}</span>)}
                            </div>
                          );
                          flushPlaceholdersFor(fname);
                        }
                      }
                      return items;
                    })()
                  )}
                  {/* Cazul când nu sunt entries deloc: renderează doar placeholderii grupați pe firma */}
                  {effectiveEntries.length === 0 && !isSunday && (() => {
                    const sorted = sortRegiiPlaceholders(regiiPlaceholdersForDay(isoDay));
                    const items = [];
                    let lastFirma = "__INIT__";
                    const overrideRegieIds = new Set((data.regii_weeks || []).map((w) => w.regie_id));
                    const overrideCountForFirma = (firma) => {
                      const seen = new Set();
                      for (const r of sorted) {
                        const f = (r.firma || "").trim() || "Fără firmă";
                        if (f === firma && overrideRegieIds.has(r.id)) seen.add(r.id);
                      }
                      return seen.size;
                    };
                    for (const r of sorted) {
                      const firma = (r.firma || "").trim() || "Fără firmă";
                      if (firma !== lastFirma) {
                        const ovCount = overrideCountForFirma(firma);
                        items.push(
                          <div
                            key={`ph-hdr-${isoDay}-${firma}`}
                            className="flex items-center gap-1 text-[8px] uppercase tracking-widest font-bold text-zinc-500 px-1 pt-1 pb-0.5"
                          >
                            <span>{firma}</span>
                            {ovCount > 0 && (
                              <span
                                className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-amber-200 text-amber-900 text-[8px] font-bold normal-case tracking-normal"
                                title={`${ovCount} ${ovCount === 1 ? "regie are" : "regii au"} necesar diferit săptămâna asta`}
                              >
                                ●{ovCount}
                              </span>
                            )}
                          </div>
                        );
                        lastFirma = firma;
                      }
                      items.push((() => {
                        const isDragging = draggingRegieId === r.id;
                        const isDragOver = dragOverRegieId === r.id && draggingRegieId && draggingRegieId !== r.id;
                        return (
                    <div
                      key={`placeholder-${r.id}-${isoDay}`}
                      role="button"
                      tabIndex={0}
                      draggable={canEditPlanning}
                      onDragStart={(ev) => {
                        if (!canEditPlanning) return;
                        setDraggingRegieId(r.id);
                        try { ev.dataTransfer.setData("text/plain", JSON.stringify({ rid: r.id, firma })); ev.dataTransfer.effectAllowed = "move"; } catch { /* noop */ }
                      }}
                      onDragEnd={() => { setDraggingRegieId(null); setDragOverRegieId(null); }}
                      onDragOver={(ev) => {
                        if (!draggingRegieId || draggingRegieId === r.id) return;
                        const srcR = visibleRegii.find((x) => x.id === draggingRegieId);
                        const srcFirma = (srcR?.firma || "").trim() || "Fără firmă";
                        if (srcFirma !== firma) return;
                        ev.preventDefault();
                        ev.dataTransfer.dropEffect = "move";
                        setDragOverRegieId(r.id);
                      }}
                      onDragLeave={() => { if (dragOverRegieId === r.id) setDragOverRegieId(null); }}
                      onDrop={(ev) => {
                        ev.preventDefault();
                        const src = draggingRegieId;
                        setDraggingRegieId(null);
                        setDragOverRegieId(null);
                        if (!src || src === r.id) return;
                        reorderRegiiInFirma(firma, src, r.id);
                      }}
                      onClick={() => setCreatingOn(isoDay)}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                          ev.preventDefault();
                          setCreatingOn(isoDay);
                        }
                      }}
                      className={`text-left rounded px-2 py-1.5 text-[11px] border-2 transition-colors cursor-pointer hover:shadow-sm ${isSaturdayIso(isoDay) ? "opacity-75" : ""} ${isDragging ? "opacity-40" : ""} ${isDragOver ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}
                      style={{ borderColor: regieColor(r), color: "white", background: regieColor(r) }}
                      data-testid={`regie-placeholder-${r.id}-${isoDay}`}
                      title={isSaturdayIso(isoDay) ? "Regie opțională sâmbăta — click pentru a aloca dacă lucrezi" : (canEditPlanning ? "Click pentru alocare · trage pentru a reordona" : "Regie activă — click pentru a aloca personal")}
                    >
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-white/30 text-white flex-shrink-0">regie</span>
                        {(() => {
                          const b = regiePatternBadge(r.id);
                          if (!b) return null;
                          return (
                            <span
                              className={`text-[8px] font-bold tracking-wide px-1 py-0.5 rounded border bg-white/25 text-white border-white/40 flex-shrink-0`}
                              title={b.title}
                              data-testid={`placeholder-pattern-${r.id}-${isoDay}`}
                            >
                              {b.label}
                            </span>
                          );
                        })()}
                        {isSaturdayIso(isoDay) && (
                          <span className="text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-white/30 text-white flex-shrink-0" title="Opțional sâmbăta">opt</span>
                        )}
                        <div className="ml-auto flex items-center gap-0.5 flex-shrink-0">
                          {canEditPlanning && (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); setEditingRegieId(r.id); }}
                              onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.stopPropagation(); ev.preventDefault(); setEditingRegieId(r.id); } }}
                              className="inline-flex items-center justify-center w-5 h-5 rounded text-[11px] hover:bg-white/30 cursor-pointer text-white"
                              title="Editează toate detaliile regiei (firma, perioada, necesarul de persoane etc.)"
                              data-testid={`placeholder-edit-full-only-${r.id}-${isoDay}`}
                            >
                              ✏️
                            </span>
                          )}
                          {canEditPlanning && (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                ev.preventDefault();
                                // 🟢 Fallback: data inițială salvată sau prima alocare existentă
                                let sd = r.start_date || "";
                                let ed = r.end_date || "";
                                const ind = !!r.indeterminate_end;
                                if (!sd || (!ed && !ind)) {
                                  const related = visibleEntries.filter((x) => x.regie_id === r.id);
                                  if (!sd) {
                                    const mins = related.map((x) => x.start_date).filter(Boolean).sort();
                                    if (mins.length) sd = mins[0];
                                  }
                                  if (!ed && !ind) {
                                    const maxs = related.map((x) => x.end_date).filter(Boolean).sort();
                                    if (maxs.length) ed = maxs[maxs.length - 1];
                                  }
                                }
                                setPostponing({
                                  type: "regie",
                                  ref_id: r.id,
                                  key: `placeholder-${r.id}`,
                                  label: r.name || r.label,
                                  currentStartDate: sd,
                                  currentEndDate: ed,
                                  currentIndeterminateEnd: ind,
                                });
                              }}
                              onKeyDown={(ev) => {
                                if (ev.key === "Enter" || ev.key === " ") {
                                  ev.stopPropagation();
                                  ev.preventDefault();
                                  ev.currentTarget.click();
                                }
                              }}
                              className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] hover:bg-white/30 cursor-pointer text-white"
                              title="Amână (mută data de început)"
                              data-testid={`placeholder-postpone-${r.id}-${isoDay}`}
                            >
                              📅
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="font-semibold text-[12px] leading-snug line-clamp-2 break-words mt-1" title={r.name || r.label}>{r.name || r.label}</div>
                      {r.vorarbeiter && (
                        <div className="text-[9px] mt-0.5 italic opacity-90" title={`Vorarbeiter: ${r.vorarbeiter}`} data-testid={`placeholder-vorarbeiter-${r.id}-${isoDay}`}>
                          👷 {r.vorarbeiter}
                        </div>
                      )}
                      {(() => {
                        const isSat = isSaturdayIso(isoDay);
                        // Necesar efectiv pentru placeholder
                        // Sâmbăta: necesarul e ÎNTOTDEAUNA 0 (regii opționale, alocare manuală)
                        const dov = (data.regii_day_overrides || []).find((o) => o.regie_id === r.id && o.date === isoDay);
                        const wkOv = (data.regii_weeks || []).find((w) => w.regie_id === r.id);
                        const minP = isSat
                          ? 0
                          : (dov && dov.min_people != null
                            ? Number(dov.min_people)
                            : (wkOv && wkOv.min_people != null
                              ? Number(wkOv.min_people)
                              : regieDefaultMinForIso(r, isoDay)));
                        const isOv = !isSat && dov && dov.min_people != null;
                        return (
                          <div className="text-[9px] opacity-90 mt-0.5 italic flex items-center gap-1">
                            <span className="flex-1">{isSat ? "click → alocă manual · 0/0" : `click → alocă · necesar ${minP} pers${isOv ? " ★" : ""}`}</span>
                            {!isSat && canEditPlanning && (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  setEditingMinPeople({
                                    target: { kind: "regie", id: r.id, name: r.name || "—" },
                                    isoDay,
                                    currentMin: minP,
                                    currentMax: Number(r.default_max_people || 0),
                                  });
                                }}
                                onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); ev.currentTarget.click(); } }}
                                className="ml-0.5 px-1 rounded hover:bg-white/30 cursor-pointer not-italic font-bold text-white"
                                title="Modifică necesarul (0 = nu mai cere persoane)"
                                data-testid={`placeholder-${r.id}-${isoDay}-edit-min`}
                              >
                                ✎
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                      );
                      })());
                    }
                    return items;
                  })()}
                  {/* Persoane în concediu / indisponibili pe această zi — vizibile dar NU numărate ca disponibile */}
                  {!isSunday && (() => {
                    const onLeave = (data.persons || [])
                      .filter((p) => !p.is_admin_user)
                      // Exclude persoanele deja concediate la sau înainte de ziua aceasta
                      .filter((p) => !p.data_concedierii || p.data_concedierii > isoDay)
                      .map((p) => {
                        const v = (p.vacations || []).find((vv) => (vv.start_date || "") <= isoDay && isoDay <= (vv.end_date || ""));
                        return v ? { person: p, vac: v } : null;
                      })
                      .filter(Boolean);
                    if (onLeave.length === 0) return null;
                    const concediu = onLeave.filter((x) => x.vac.kind !== "unavailable");
                    const indisp = onLeave.filter((x) => x.vac.kind === "unavailable");
                    const renderGroup = (label, items, bgClass, icon) => {
                      if (items.length === 0) return null;
                      return (
                        <div className={`rounded ${bgClass} px-1.5 py-1 text-[9px]`} title={items.map((x) => x.person.name).join(", ")}>
                          <div className="flex items-center gap-1 font-bold uppercase tracking-wider mb-0.5">
                            <span>{icon}</span>
                            <span>{label} ({items.length})</span>
                          </div>
                          <div className="leading-tight opacity-90 line-through">
                            {items.slice(0, 4).map((x) => x.person.name.split(" ").slice(0, 2).join(" ")).join(", ")}
                            {items.length > 4 ? ` +${items.length - 4}` : ""}
                          </div>
                        </div>
                      );
                    };
                    return (
                      <div className="mt-1 space-y-1" data-testid={`day-onleave-${isoDay}`}>
                        {renderGroup("Concediu", concediu, isSunday ? "bg-blue-900/40 text-blue-200 border border-blue-700/40" : "bg-blue-50 text-blue-800 border border-blue-200", "🏖")}
                        {renderGroup("Indisponibili", indisp, isSunday ? "bg-amber-900/40 text-amber-200 border border-amber-700/40" : "bg-amber-50 text-amber-800 border border-amber-200", "🚫")}
                      </div>
                    );
                  })()}
                  {entries.length > 0 && (
                    <button
                      onClick={() => setSharing({ day: isoDay })}
                      className="mt-1 inline-flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-green-600 hover:bg-green-700 rounded py-1.5 px-2 transition-colors"
                      data-testid={`share-day-${isoDay}`}
                    >
                      <Send size={11}/> TRIMITE
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          </div>
          )}
        </div>

        {/* Sidebar — free resources */}
        <SidebarSummary
          freePersons={freePersons}
          busyPersons={busyPersons}
          onLeavePersons={onLeavePersons}
          freeCars={freeCars}
        />
      </div>

      {editing && (
        <AssignDialog
          entry={editing}
          persons={data.persons}
          cars={data.cars}
          weekStart={weekStart}
          regiiWeeks={data.regii_weeks || []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {creatingOn && (
        <CreateDayDialog
          day={creatingOn}
          projects={visibleProjects}
          regii={visibleRegii}
          persons={data.persons}
          cars={data.cars}
          entries={visibleEntries}
          regiiWeeks={data.regii_weeks || []}
          regiiDayOverrides={data.regii_day_overrides || []}
          projectsDayOverrides={data.projects_day_overrides || []}
          onClose={() => setCreatingOn(null)}
          onSaved={() => { setCreatingOn(null); load(); }}
          onRefresh={() => load()}
        />
      )}

      {toolHistoryOpen && <ToolHistoryDialog onClose={() => setToolHistoryOpen(false)} />}
      {historyRegie && (
        <RegieDetailDialog regie={historyRegie} onClose={() => setHistoryRegie(null)} />
      )}
      {sharing && (
        <ShareDayDialog
          day={sharing.day}
          entries={visibleEntries.filter((e) => (e.start_date || "") <= sharing.day && sharing.day <= (e.end_date || ""))}
          personsIndex={Object.fromEntries((data.persons || []).map((p) => [p.id, p.name]))}
          carsIndex={Object.fromEntries((data.cars || []).map((c) => [c.id, {
            label: c.label,
            defect_reason: c.defect_reason || "",
            defects: c.defects || [],
            status: c.status || "functional",
          }]))}
          projectsIndex={Object.fromEntries((projects || []).map((p) => [p.id, {
            name: p.name,
            adresa: p.adresa,
            zones: p.zones || [],
            work_zones: p.work_zones || "",
            special: p.special || "",
            utilities: p.utilities || null,
            bauherr: p.bauherr || "",
            bags_festgebunden: p.bags_festgebunden || 0,
            bags_schwachgebunden: p.bags_schwachgebunden || 0,
            requires_post_measurement: p.requires_post_measurement,
            is_ruckbau: p.is_ruckbau || false,
          }]))}
          regiiIndex={Object.fromEntries((regii || []).map((r) => [r.id, { name: r.name, adresa: r.adresa, firma: r.firma, vorarbeiter: r.vorarbeiter }]))}
          regiiOrder={(regii || []).map((r) => r.id)}
          projectsOrder={(projects || []).map((p) => p.id)}
          freePersons={freePersonsForDay(sharing.day)}
          company={company}
          logoDataUrl={logoDataUrl}
          onClose={() => setSharing(null)}
        />
      )}

      {copyingFrom && (
        <CopyDayDialog
          fromDate={copyingFrom}
          weekDays={weekDays.map(toISO)}
          entries={visibleEntries.filter((e) => (e.start_date || "") <= copyingFrom && copyingFrom <= (e.end_date || ""))}
          onClose={() => setCopyingFrom(null)}
          onSaved={() => { setCopyingFrom(null); load(); }}
        />
      )}

      <PostponeDialog
        open={!!postponing}
        onClose={() => setPostponing(null)}
        card={postponing}
        currentStartDate={postponing?.currentStartDate || ""}
        currentEndDate={postponing?.currentEndDate || ""}
        currentIndeterminateEnd={!!postponing?.currentIndeterminateEnd}
        earliestAllocationDate={(() => {
          if (!postponing?.ref_id) return null;
          const refKey = postponing.type === "regie" ? "regie_id" : "project_id";
          // Cea mai veche dată a unei alocări cu oameni efectiv (person_ids.length > 0)
          const related = visibleEntries.filter((x) => x[refKey] === postponing.ref_id && (x.person_ids || []).length > 0);
          if (related.length === 0) return null;
          const dates = related.map((x) => x.start_date).filter(Boolean).sort();
          return dates[0] || null;
        })()}
        onSaved={() => { setPostponing(null); load(); }}
      />

      <EditMinPeopleDialog
        open={!!editingMinPeople}
        onClose={() => setEditingMinPeople(null)}
        target={editingMinPeople?.target}
        isoDay={editingMinPeople?.isoDay}
        currentMin={editingMinPeople?.currentMin}
        currentMax={editingMinPeople?.currentMax}
        onSaved={() => { setEditingMinPeople(null); load(); }}
      />

      <RegieFullDialog
        open={!!editingRegieId}
        regieId={editingRegieId}
        onOpenChange={(v) => { if (!v) setEditingRegieId(null); }}
        onUpdated={() => { setEditingRegieId(null); load(); }}
      />

      {loading && <div className="fixed bottom-4 right-4 bg-white border border-[var(--border)] rounded-md shadow px-3 py-1.5 text-xs text-zinc-600">Se încarcă…</div>}
    </div>
  );
}


// Categoriile excluse pentru "+ pe zi" — proiecte deja terminate/demontate

// Quick edit popover — modifici câmpurile cele mai folosite (data sfârșit, min/max persoane)
// fără a deschide pagina completă de Proiecte. Folosit în CreateDayDialog.







